import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Microphone, Stop, ArrowLeft, ArrowsDownUp, Translate, SpinnerGap,
  SpeakerHigh, Warning, CheckCircle, Sparkle, CaretDown
} from '@phosphor-icons/react';
import { toast } from '../hooks/use-toast';
import axiosInstance from '../api/api';

const LANGUAGES = {
  en: { label: 'English', flag: '🇺🇸', color: '#2B4C6F', short: 'EN' },
  es: { label: 'Spanish', flag: '🇲🇽', color: '#D95800', short: 'ES' },
  pt: { label: 'Portuguese', flag: '🇧🇷', color: '#059669', short: 'PT' },
  fr: { label: 'French', flag: '🇫🇷', color: '#4F6BED', short: 'FR' },
  pl: { label: 'Polish', flag: '🇵🇱', color: '#DC2626', short: 'PL' },
  ru: { label: 'Russian', flag: '🇷🇺', color: '#7C3AED', short: 'RU' },
  uk: { label: 'Ukrainian', flag: '🇺🇦', color: '#EAB308', short: 'UK' },
  zh: { label: 'Chinese', flag: '🇨🇳', color: '#E11D48', short: 'ZH' },
  vi: { label: 'Vietnamese', flag: '🇻🇳', color: '#F59E0B', short: 'VI' },
  ko: { label: 'Korean', flag: '🇰🇷', color: '#0EA5E9', short: 'KO' },
  tl: { label: 'Tagalog', flag: '🇵🇭', color: '#10B981', short: 'TL' },
  ht: { label: 'Haitian Creole', flag: '🇭🇹', color: '#8B5CF6', short: 'HT' },
};

// Permission / browser support helpers
const isMediaSupported = () => !!(navigator.mediaDevices && window.MediaRecorder);

export default function VoiceTranslator() {
  const navigate = useNavigate();
  const [sourceLang, setSourceLang] = useState('en');
  const [targetLang, setTargetLang] = useState('es');
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [exchanges, setExchanges] = useState([]); // ephemeral, in-memory only
  const [mediaError, setMediaError] = useState('');
  const [authorized, setAuthorized] = useState(null); // null = checking
  const [audioLevel, setAudioLevel] = useState(0);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const levelTimerRef = useRef(null);
  const playerRef = useRef(null);
  const waCtxRef = useRef(null);

  // Authorize: contractor or worker only
  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (!userStr) {
      setAuthorized(false);
      return;
    }
    try {
      const u = JSON.parse(userStr);
      setAuthorized(u.user_type === 'contractor' || u.user_type === 'worker');
    } catch {
      setAuthorized(false);
    }
  }, []);

  // Cleanup mic stream on unmount (ephemeral — no persistence)
  useEffect(() => {
    return () => {
      stopAudioPipeline();
      setExchanges([]);
    };
  }, []);

  const swapLang = () => {
    if (recording || processing) return;
    setSourceLang(targetLang);
    setTargetLang(sourceLang);
  };

  const pickLang = (which, code) => {
    if (recording || processing) return;
    if (which === 'source') {
      if (code === targetLang) setTargetLang(sourceLang);
      setSourceLang(code);
    } else {
      if (code === sourceLang) setSourceLang(targetLang);
      setTargetLang(code);
    }
  };

  const stopAudioPipeline = () => {
    if (levelTimerRef.current) {
      cancelAnimationFrame(levelTimerRef.current);
      levelTimerRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try { mediaRecorderRef.current.stop(); } catch {}
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (audioContextRef.current) {
      try { audioContextRef.current.close(); } catch {}
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    setAudioLevel(0);
  };

  const startLevelMeter = (stream) => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      audioContextRef.current = ctx;
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      src.connect(analyser);
      analyserRef.current = analyser;
      const data = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        if (!analyserRef.current) return;
        analyserRef.current.getByteFrequencyData(data);
        const avg = data.reduce((a, b) => a + b, 0) / data.length;
        setAudioLevel(Math.min(100, (avg / 128) * 100));
        levelTimerRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch (e) {
      // Level meter is optional — silently skip if not supported
    }
  };

  // Valid silent clip used to "unlock" audio playback inside the user's tap gesture
  // (iOS Safari / mobile Chrome block .play() calls that happen after an await).
  const SILENT_WAV = 'data:audio/wav;base64,UklGRjQAAABXQVZFZm10IBAAAAABAAEAQB8AAIA+AAACABAAZGF0YRAAAAAAAAAAAAAAAAAAAAAAAAAA';

  const unlockAudio = () => {
    try {
      if (!playerRef.current) playerRef.current = new Audio();
      const p = playerRef.current;
      p.src = SILENT_WAV;
      p.muted = false;
      p.volume = 1;
      const attempt = p.play();
      if (attempt?.then) attempt.then(() => {}).catch(() => {});
    } catch {}
  };

  const startRecording = async () => {
    if (recording || processing) return;
    unlockAudio();
    setMediaError('');
    if (!isMediaSupported()) {
      setMediaError('Voice recording is not supported in this browser. Try Chrome or Safari.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      audioChunksRef.current = [];

      // Prefer webm/opus if available; fall back to default
      const mime =
        MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
          ? 'audio/webm;codecs=opus'
          : MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : MediaRecorder.isTypeSupported('audio/mp4')
          ? 'audio/mp4'
          : '';
      const recorder = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      recorder.onstop = handleRecordingStop;
      recorder.start();
      startLevelMeter(stream);
      setRecording(true);
    } catch (err) {
      console.error('Mic permission error:', err);
      setMediaError(
        err.name === 'NotAllowedError'
          ? 'Microphone access was blocked. Allow microphone permissions and try again.'
          : 'Could not access the microphone.'
      );
    }
  };

  const stopRecording = () => {
    if (!recording) return;
    unlockAudio();
    setRecording(false);
    // Closes the recorder, mic tracks AND the level-meter AudioContext —
    // an open mic AudioContext keeps Android in "communication" audio mode and silences playback.
    stopAudioPipeline();
  };

  const handleRecordingStop = async () => {
    const chunks = audioChunksRef.current;
    audioChunksRef.current = [];
    if (!chunks.length) return;

    const blob = new Blob(chunks, { type: mediaRecorderRef.current?.mimeType || 'audio/webm' });
    if (blob.size < 1000) {
      toast({ title: 'Too short', description: 'Hold the mic a little longer.', variant: 'destructive' });
      return;
    }

    setProcessing(true);
    const capturedSource = sourceLang;
    const capturedTarget = targetLang;
    const placeholderId = `pending-${Date.now()}`;
    setExchanges(prev => [
      { id: placeholderId, pending: true, source_lang: capturedSource, target_lang: capturedTarget },
      ...prev,
    ]);

    try {
      const ext = (blob.type.includes('mp4') ? 'mp4' : blob.type.includes('wav') ? 'wav' : 'webm');
      const fd = new FormData();
      fd.append('audio', blob, `speech.${ext}`);
      fd.append('source_lang', capturedSource);
      fd.append('target_lang', capturedTarget);

      const resp = await axiosInstance.post('/translator/translate', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });

      if (resp.data?.success) {
        const ex = {
          id: `ex-${Date.now()}`,
          source_lang: resp.data.source_lang,
          target_lang: resp.data.target_lang,
          transcript: resp.data.transcript,
          translation: resp.data.translation,
          audio_base64: resp.data.audio_base64,
          audio_mime: resp.data.audio_mime || 'audio/mpeg',
          duration_ms: resp.data.duration_ms,
        };
        setExchanges(prev => [ex, ...prev.filter(e => e.id !== placeholderId)]);
        // Auto-play translated audio
        if (resp.data.audio_base64) {
          autoPlay(resp.data.audio_base64, resp.data.audio_mime || 'audio/mpeg');
        }
      } else {
        setExchanges(prev => prev.filter(e => e.id !== placeholderId));
        toast({ title: 'Could not understand', description: "We couldn't catch that. Try again.", variant: 'destructive' });
      }
    } catch (err) {
      setExchanges(prev => prev.filter(e => e.id !== placeholderId));
      toast({
        title: 'Translation failed',
        description: err.response?.data?.detail || 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setProcessing(false);
    }
  };

  const b64ToBytes = (b64) => {
    const bytes = atob(b64);
    const arr = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
    return arr;
  };

  const toBlobUrl = (b64, mime) => URL.createObjectURL(new Blob([b64ToBytes(b64)], { type: mime || 'audio/mpeg' }));

  // Fallback: decode + play through WebAudio (reliable on Android webviews when <audio> stays silent)
  const webAudioPlay = async (b64) => {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return false;
    try {
      if (!waCtxRef.current || waCtxRef.current.state === 'closed') waCtxRef.current = new Ctx();
      const ctx = waCtxRef.current;
      if (ctx.state === 'suspended') await ctx.resume();
      const buf = await ctx.decodeAudioData(b64ToBytes(b64).buffer);
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.connect(ctx.destination);
      src.start(0);
      return true;
    } catch {
      return false;
    }
  };

  const autoPlay = (b64, mime, isReplay = false) => {
    try {
      if (!playerRef.current) playerRef.current = new Audio();
      const audio = playerRef.current;
      audio.pause();
      if (audio.src?.startsWith('blob:')) URL.revokeObjectURL(audio.src);
      audio.src = toBlobUrl(b64, mime);
      audio.muted = false;
      audio.volume = 1;
      const attempt = audio.play();
      // If <audio> never actually starts producing sound, retry through WebAudio
      const watchdog = setTimeout(async () => {
        if (audio.currentTime === 0 || audio.paused) {
          audio.pause();
          const ok = await webAudioPlay(b64);
          if (!ok && !isReplay) {
            toast({
              title: 'Tap Replay to hear it',
              description: 'Your browser blocked automatic audio — tap the Replay button on the translation.',
            });
          }
        }
      }, 1200);
      if (attempt?.catch) {
        attempt.catch(async () => {
          clearTimeout(watchdog);
          const ok = await webAudioPlay(b64);
          if (!ok) {
            toast({
              title: isReplay ? 'Playback failed' : 'Tap Replay to hear it',
              description: isReplay
                ? 'Your device blocked audio playback. Check media volume and try again.'
                : 'Your browser blocked automatic audio — tap the Replay button on the translation.',
              variant: isReplay ? 'destructive' : undefined,
            });
          }
        });
      }
    } catch {}
  };

  const replay = (ex) => {
    if (!ex.audio_base64) return;
    autoPlay(ex.audio_base64, ex.audio_mime, true);
  };

  if (authorized === null) return null;
  if (authorized === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-900 text-white p-6" data-testid="translator-unauthorized">
        <div className="max-w-md text-center">
          <Warning size={48} weight="bold" className="text-amber-400 mx-auto mb-4" />
          <h2 className="text-2xl font-black mb-2">Contractors & workers only</h2>
          <p className="text-neutral-400 mb-6">The voice translator is reserved for contractor and worker accounts.</p>
          <button onClick={() => navigate('/')} className="h-12 px-6 bg-[#D95800] text-white font-bold rounded-lg border-2 border-black shadow-[3px_3px_0px_#000]">Return Home</button>
        </div>
      </div>
    );
  }

  const src = LANGUAGES[sourceLang];
  const tgt = LANGUAGES[targetLang];

  return (
    <div className="min-h-screen bg-[#F2F2F7]" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, system-ui, sans-serif' }} data-testid="voice-translator">
      {/* Header */}
      <header className="bg-[#F2F2F7]/90 backdrop-blur-xl sticky top-0 z-10 border-b border-black/5">
        <div className="max-w-xl mx-auto px-5 py-4 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-[15px] font-semibold text-[#EA580C]" data-testid="translator-back-btn">
            <ArrowLeft size={18} weight="bold" /> Back
          </button>
          <div className="flex items-center gap-2">
            <Translate size={20} weight="fill" className="text-[#EA580C]" />
            <h1 className="text-[17px] font-bold text-neutral-900">Voice Translator</h1>
          </div>
          <span className="text-[10px] font-semibold uppercase tracking-wide text-neutral-400">Live</span>
        </div>
      </header>

      <div className="max-w-xl mx-auto px-5 py-6">
        {/* You speak */}
        <p className="text-[13px] font-bold uppercase tracking-[0.12em] text-neutral-800 mb-2 px-1" data-testid="lang-swap">You Speak</p>
        <div className="relative" data-testid="source-lang-card">
          <div className="bg-white rounded-2xl shadow-[0_1px_3px_rgba(0,0,0,0.08)] px-4 h-16 flex items-center gap-3">
            <span className="w-10 h-10 rounded-full bg-[#F2F2F7] flex items-center justify-center text-2xl leading-none">{src.flag}</span>
            <span className="flex-1 text-[22px] font-semibold text-neutral-900">{src.label}</span>
            <CaretDown size={18} weight="bold" className="text-neutral-400" />
          </div>
          <select
            value={sourceLang}
            onChange={(e) => pickLang('source', e.target.value)}
            disabled={recording || processing}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
            data-testid="source-lang-select"
            aria-label="Language you speak"
          >
            {Object.entries(LANGUAGES).map(([code, l]) => (
              <option key={code} value={code}>{l.flag} {l.label}</option>
            ))}
          </select>
        </div>

        {/* Divider with swap */}
        <div className="relative my-5 flex items-center justify-center">
          <div className="absolute inset-x-0 h-px bg-neutral-300/70" />
          <button
            onClick={swapLang}
            disabled={recording || processing}
            className="relative w-12 h-12 rounded-full bg-white shadow-[0_2px_8px_rgba(0,0,0,0.10)] flex items-center justify-center text-neutral-500 hover:text-[#EA580C] active:scale-95 transition-all disabled:opacity-40"
            data-testid="swap-lang-btn"
            aria-label="Swap languages"
          >
            <ArrowsDownUp size={20} weight="bold" />
          </button>
        </div>

        {/* They hear */}
        <p className="text-[13px] font-bold uppercase tracking-[0.12em] text-neutral-800 mb-2 px-1">They Hear</p>
        <div className="relative" data-testid="target-lang-card">
          <div className="bg-white rounded-2xl shadow-[0_1px_3px_rgba(0,0,0,0.08)] px-4 h-16 flex items-center gap-3">
            <span className="w-10 h-10 rounded-full bg-[#F2F2F7] flex items-center justify-center text-2xl leading-none">{tgt.flag}</span>
            <span className="flex-1 text-[22px] font-semibold text-neutral-900">{tgt.label}</span>
            <CaretDown size={18} weight="bold" className="text-neutral-400" />
          </div>
          <select
            value={targetLang}
            onChange={(e) => pickLang('target', e.target.value)}
            disabled={recording || processing}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
            data-testid="target-lang-select"
            aria-label="Language they hear"
          >
            {Object.entries(LANGUAGES).map(([code, l]) => (
              <option key={code} value={code}>{l.flag} {l.label}</option>
            ))}
          </select>
        </div>

        {mediaError && (
          <div className="mt-5 bg-red-50 border border-red-200 rounded-2xl p-4 flex items-start gap-3" data-testid="media-error">
            <Warning size={20} weight="fill" className="text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{mediaError}</p>
          </div>
        )}

        {/* BIG mic button */}
        <div className="flex flex-col items-center justify-center mt-10 mb-6" data-testid="mic-zone">
          <button
            onClick={recording ? stopRecording : startRecording}
            disabled={processing}
            className={`relative w-36 h-36 rounded-full transition-all flex items-center justify-center select-none ${
              recording
                ? 'bg-red-500 shadow-[0_0_0_12px_rgba(239,68,68,0.15),0_8px_24px_rgba(239,68,68,0.4)] animate-pulse'
                : processing
                ? 'bg-neutral-300'
                : 'bg-[#EA580C] shadow-[0_0_0_12px_rgba(234,88,12,0.12),0_10px_30px_rgba(234,88,12,0.35)] hover:scale-105 active:scale-95'
            }`}
            data-testid={recording ? 'stop-recording-btn' : 'start-recording-btn'}
            aria-label={recording ? 'Stop recording' : 'Start recording'}
          >
            {recording && (
              <span
                className="absolute inset-0 rounded-full border-2 border-red-300"
                style={{ transform: `scale(${1 + audioLevel / 200})`, opacity: 0.6 }}
              />
            )}
            {processing ? (
              <SpinnerGap size={56} weight="bold" className="animate-spin text-neutral-500" />
            ) : recording ? (
              <Stop size={56} weight="fill" className="text-white" />
            ) : (
              <Microphone size={56} weight="fill" className="text-white" />
            )}
          </button>
          <p className="mt-5 text-[15px] font-semibold text-neutral-600">
            {processing ? 'Translating…' : recording ? 'Listening… tap to stop' : `Tap to speak ${src.label}`}
          </p>
          {!recording && !processing && (
            <p className="mt-1 text-xs text-neutral-400 flex items-center gap-1">
              <Sparkle size={10} weight="fill" className="text-[#EA580C]" /> Nothing is saved — conversations stay private
            </p>
          )}
        </div>

        {/* Conversation history */}
        <div className="space-y-3 mt-4 pb-10" data-testid="exchanges">
          {exchanges.length === 0 && !processing && (
            <div className="text-center py-10 text-neutral-400 bg-white/60 rounded-2xl">
              <Translate size={30} weight="bold" className="mx-auto mb-2 opacity-40" />
              <p className="text-sm font-medium">No conversation yet. Tap the mic to start.</p>
            </div>
          )}
          {exchanges.map((ex) => (
            <ExchangeCard key={ex.id} ex={ex} onReplay={() => replay(ex)} />
          ))}
        </div>
      </div>
    </div>
  );
}

function ExchangeCard({ ex, onReplay }) {
  const src = LANGUAGES[ex.source_lang] || LANGUAGES.en;
  const tgt = LANGUAGES[ex.target_lang] || LANGUAGES.es;

  if (ex.pending) {
    return (
      <div className="bg-white rounded-2xl shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4 flex items-center gap-3" data-testid="exchange-pending">
        <SpinnerGap size={22} weight="bold" className="animate-spin text-[#EA580C]" />
        <p className="text-sm text-neutral-500">Transcribing → translating → speaking…</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-[0_1px_3px_rgba(0,0,0,0.08)] overflow-hidden" data-testid={`exchange-${ex.id}`}>
      {/* Source (what user said) */}
      <div className="p-4 border-b border-neutral-100">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[11px] font-bold uppercase tracking-wide text-neutral-400">
            {src.flag} {src.label} — you said
          </span>
          {ex.duration_ms !== undefined && (
            <span className="text-[10px] text-neutral-400">{(ex.duration_ms / 1000).toFixed(1)}s</span>
          )}
        </div>
        <p className="text-[15px] text-neutral-600 leading-snug">{ex.transcript || <span className="italic text-neutral-400">[empty]</span>}</p>
      </div>
      {/* Target (translation) */}
      <div className="p-4 bg-orange-50/50">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[11px] font-bold uppercase tracking-wide text-[#EA580C]">
            {tgt.flag} {tgt.label} — they hear
          </span>
          {ex.audio_base64 ? (
            <button
              onClick={onReplay}
              className="flex items-center gap-1 text-[11px] font-bold text-[#EA580C] active:opacity-60"
              data-testid="replay-btn"
            >
              <SpeakerHigh size={13} weight="fill" /> Replay
            </button>
          ) : (
            <span className="text-[10px] font-bold uppercase text-neutral-400" data-testid="no-audio-note">
              Voice unavailable — text only
            </span>
          )}
        </div>
        <p className="text-[19px] font-semibold text-neutral-900 leading-snug">{ex.translation || <span className="italic text-neutral-400">[empty]</span>}</p>
        {ex.translation && !ex.translation.startsWith('[') && (
          <div className="mt-1.5 flex items-center gap-1 text-[10px] text-emerald-600">
            <CheckCircle size={12} weight="fill" /> Auto-played
          </div>
        )}
      </div>
    </div>
  );
}
